import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Button, Spin } from "antd";
import Icon, { ExclamationCircleFilled, SyncOutlined } from "src/icons";
import { RouteComponentProps } from "react-router";
import ReactGA from "react-ga4";
import Tabs, { TabData } from "src/shared/components/Tabs";

import "src/scenes/Imcom/Details/index.scss";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import DetailsScroller from "src/scenes/Imcom/Details/DetailsScroller";
import { SelectedFilterValues } from "src/shared/components/FiltersPanev2";

import { REFRESH_ICON_ANIMATION, RunningAnobisMode, ThisWeekTabNames } from "src/shared/config";
import { DetailMetricType } from "src/shared/models/DetailsModel";
import { useMetricsMetadata } from "src/shared/hooks";
import { truncate, safeGet, reduxOperationToStatus, mergeStatuses } from "src/shared/utils";
import {
  setFiltersFromL2Page,
  setFilters,
  fetchDetails,
  fetchNestedTreeData,
  clearDetails,
  addPin,
  setDimensions,
  filtersReset,
  FetchDetailsPayload,
  fetchTableCharts,
  FetchDistFiltersPayload,
} from "src/store/details";
import { changeTimeTabAppWide, filtersConfigReset, setComingBackFromDetailsPage, UserRoles } from "src/store/app";
import { useRedux } from "src/shared/hooks";
import { IRootState } from "src/shared/types";
import { IReduxOperations, IActionFactory, IObject } from "src/store";

import { animated, useSpring } from "react-spring";
import styled from "@emotion/styled";
import colors from "src/styles/variables.scss";
import { getUserRoles } from "src/api/app";
import MobileTable from "src/scenes/Imcom/Details/MobileTable.jsx";
import { HomeFilled } from "@ant-design/icons";
import { devLogging } from "src/utils/logger";
import { useLocation } from "react-router-dom";

const DEFAULT_COMPARE_NO = 2;

/**
 * Refer to wiki on bitbucket to understand filter logic
 * Metric type(metric vs pinned metric) is decided from the url
 * Different filter logic for both types of metrics
 * Note that once the page is rendered, and filters are populated, the filter can be changed
 */

const IconHolder = styled.div`
  font-size: 16px;
  line-height: 13px;
`;

const CALL_DELAY = 1500;

type OptionsType = {
  className: string;
  iconClass: string;
  handleClick(): void;
};
type OptionGroupType = Array<OptionsType>;

type Tabtypes = {
  tabName: string;
}[];

const TOP_CHARTS = [
  { label: "NOW", valueField: "bigNumber", percentage: "vCompare" },
  // {label: "CURRENT GOAL", valueField: "currentGoal"},
  { label: "BENCHMARK", valueField: "benchMark" },
];

interface IDetailsMappedProps {
  charts: IReduxOperations;
  tableCharts: IObject<any>;
  filters: IObject<any>;
  l2Filters: IObject<any>;
  distributedFilters: IReduxOperations;
  selectedDimensions: Array<string>;
  selectedTab: string;
  filtersConfig: IReduxOperations;
  userRoles: IReduxOperations<UserRoles>;
}

/** TODO: Replace all the any types */
/** TODO: Remove all the unused Vars */
type MappedActionProps = {
  setFiltersFromL2Page();
  setFilters: IActionFactory<symbol, IObject<any>>;
  fetchDetails(payload: FetchDetailsPayload);
  fetchTableCharts(payload: any);
  fetchNestedTreeData(payload: FetchDistFiltersPayload);
  addPin(payload: string);
  clearDetails: IActionFactory<symbol, any>;
  setDimensions: IActionFactory<symbol, Array<string>>;
  filtersReset: IActionFactory<symbol, any>;
  filtersConfigReset: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};
const DetailsContainer: React.FC<RouteComponentProps<{ id: string; type: DetailMetricType; storeId: string }>> = ({
  history,
  match,
}) => {
  useEffect(() => {
    // ReactGA.pageview(window.location.pathname, [], "Details");
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "IMCom Fourth Screen" });
  }, []);

  // FIXME: Catch if `metricOrPinId` not a number
  const { id: metricOrPinId, type: metricType, storeId: podId } = match.params;
  devLogging("metricOrPinId", metricOrPinId, metricType, podId);
  const [showDialog, setShowDialog] = useState(false); // controls visibility of filter pane
  const [showModal, setShowModal] = useState(false); // controls visibility of add pin modal
  const [modalPause, setModalPause] = useState(false); // retain modal info if filter pane opened from the modal, reopen modal after filter is closed
  const [pauseData, setPauseData] = useState(""); // same as above, saves name field  const lineChartContainer = useRef<HTMLDivElement>(null);
  const lineChartContainer = useRef<HTMLDivElement>(null);
  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [metrics, metricsStatus] = useMetricsMetadata();

  const {
    setFiltersFromL2Page: setFiltersFromL2,
    filters,
    l2Filters,
    tableCharts,
    charts,
    selectedTab,
    setFilters: boundSetFilters,
    fetchDetails: fetchMetricDetails,
    fetchTableCharts: fetchTableChartsDetails,
    distributedFilters,
    fetchNestedTreeData: fetchDistributedFilters,
    addPin: addNewPin,
    setDimensions: setDimensionsAction,
    selectedDimensions,
    filtersReset: filtersResetAction,
    changeTimeTabAppWide: changeTimeTab,
    setComingBackFromDetailsPage: toggleComingBackFromDetailsPage,
    // filtersConfigReset: filtersConfigResetAction,
    filtersConfig,
    userRoles,
  } = useRedux<IRootState, IDetailsMappedProps, MappedActionProps>(
    (state) => ({
      filters: state.metricDetails.filters,
      l2Filters: state.category.filters,
      charts: state.metricDetails.charts,
      tableCharts: state.metricDetails.tableCharts,
      distributedFilters: state.metricDetails.distributedFilters,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      selectedDimensions: state.metricDetails.selectedDimensions,
      filtersConfig: state.app.filtersConfig,
      userRoles: state.app.userRoles,
    }),
    {
      setFiltersFromL2Page,
      setFilters,
      fetchDetails,
      fetchTableCharts,
      fetchNestedTreeData,
      clearDetails,
      addPin,
      setDimensions,
      filtersReset,
      filtersConfigReset,
      changeTimeTabAppWide,
      setComingBackFromDetailsPage,
    }
  );

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // To avoid few side effects of state change on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // users are always pain in the a**
    // They might visit category page (L2) and then directly visit pin detail page by typing URL

    if (metricType !== DetailMetricType.PIN) {
      setFiltersFromL2();
    }

    const storeIdFilter: SelectedFilterValues = {
      store_id: [podId],
    };

    fetchMetricDetails({
      fresh: true,
      metricId: metricOrPinId,
      filters: storeIdFilter,
      metricType,
    });

    devLogging("fetch charts ", charts, podId);

    if (safeGet(charts, "payload.tableChartId") && safeGet(charts, "payload.metricId")) {
      fetchTableChartsDetails({
        tableChartId: safeGet(charts, "payload.tableChartId"),
        metricId: safeGet(charts, "payload.metricId"),
        filters: [
          {
            field: "store_id",
            value: [podId],
          },
          // TO be enabled
          // {
          //   "field": "city",
          //   "value": userRoles.payload.cities || []
          // }
        ],
        metricType,
      });
    }
    devLogging("exported statte value of table chart ", tableCharts);
  }, [safeGet(charts, "payload.metricId"), safeGet(charts, "payload.tableChartId")]);

  /** Removing the selected dimensions on page navigation/component unmount(and not on refresh) */
  useEffect(() => {
    return () => {
      setDimensionsAction([]);
      filtersResetAction();
      filtersConfigReset();
    };
  }, []);

  const badgeRef = useRef();
  const applyRef = (target) => {
    if (!badgeRef.current) {
      badgeRef.current = target && target.getBoundingClientRect();
    }
  };

  const onRefresh = useCallback(() => {
    setIsRefreshClicked(Date.now());

    setTimeout(() => {
      fetchMetricDetails({
        metricId: metricOrPinId,
        filters: filters,
        fresh: true,
        metricType,
      });
      fetchTableChartsDetails({
        tableChartId: safeGet(charts, "payload.tableChartId"),
        metricId: safeGet(charts, "payload.metricId"),
        filters: [
          {
            field: "store_id",
            value: userRoles.payload.pods || [],
          },
          {
            field: "city",
            value: userRoles.payload.cities || [],
          },
        ],
        metricType,
      });
    }, CALL_DELAY);
  }, [filters]);

  useEffect(() => {
    if (isRefreshClicked && Date.now() - isRefreshClicked > CALL_DELAY && safeGet(charts, "pending") === false) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(charts, "pending"), isRefreshClicked]);

  const handleChangeTab = useCallback(
    (tabData: TabData) => {
      changeTimeTab(tabData.key);
      fetchMetricDetails({ metricId: metricOrPinId, filters, metricType });
      filtersResetAction();
    },
    [filters, metricType, metricOrPinId]
  );

  const isFilterApplied = useMemo(
    () => Object.entries(filters).some(([key, value]: [string, any]): boolean => value.length !== 0),
    [filters]
  );

  const options: OptionGroupType = [
    {
      className: "option1",
      iconClass: "home",
      handleClick: () => {
        history.push("/im-com-app");
        window.location.reload();
      },
    },
    {
      className: "option2",
      iconClass: "sync",
      handleClick: onRefresh,
    },
  ];

  const onBack = useCallback(() => {
    toggleComingBackFromDetailsPage(true);
    history.goBack();
  }, [history]);

  const handleSubmitPin = useCallback(
    (metricName) => {
      const chartData = charts.payload;
      const newPin = {
        metricId: metricType === DetailMetricType.PIN ? safeGet(charts, "payload.metricId", 0) : metricOrPinId,
        label: metricName,
        filters: JSON.stringify(
          Object.entries(filters).map(([key, value]) => ({
            field: key,
            value: value,
          }))
        ),
        performanceChartIds: JSON.stringify([chartData.currentGoal, chartData.benchMark]),
        isPercentage: safeGet(charts, "payload.isPercentage", false),
        currentChartId: chartData.currentChartId,
        pastChartId: chartData.pastChartId,
        trendChartId: chartData.trendChartId,
        isPositive: chartData.isPositive,
      };
      setShowModal(false);
      addNewPin(JSON.stringify(newPin));
      setModalPause(false);
      setPauseData("");
    },
    [charts, filters]
  );

  const onModalClose = useCallback(() => {
    setShowModal(false);
    setModalPause(false);
    setPauseData("");
  }, []);

  const handleChainedDropdownChange = useCallback(
    (payload: string[]) => {
      setDimensionsAction(payload);
      if (!safeGet(payload, "0")) {
        filtersResetAction();
        return;
      }
      const applyPodFilters = [
        {
          field: "store_id",
          value: userRoles.payload?.pods || [],
        },
      ];
      devLogging("handleChainedDropdownChange", payload, filters, applyPodFilters, userRoles.payload?.pods);

      fetchDistributedFilters({
        tableChartId: safeGet(charts, "payload.tableChartId"),
        filters,
        selectedTab,
        firstSelectedValue: safeGet(payload, "0"),
        secondSelectedValue: safeGet(payload, "1"),
        lastSelectedValue: safeGet(payload, "2"),
        metricId: charts.payload.metricId, // FIXME: What if payload not yet preset
        ...(metricType === DetailMetricType.PIN && { pinId: charts.payload.id }),
      });
    },
    [setDimensionsAction, fetchDistributedFilters, charts, filters, selectedTab, metricOrPinId]
  );

  const filterSliceId = filtersConfig.success
    ? (filtersConfig.payload || {}).config?.data?.meta?.queryParams?.form_data?.slice_id || 0
    : 0;
  const detailStatus = reduxOperationToStatus(charts);
  const filtersConfigStatus = reduxOperationToStatus(filtersConfig);

  const syncIcon = (
    <IconHolder>
      {safeGet(charts, "payload.isStaleMetric", false) && !isRefreshClicked ? (
        <ExclamationCircleFilled />
      ) : (
        <SyncOutlined />
      )}
    </IconHolder>
  );

  // ********** Animations Start ************

  const iconStyles = useSpring(REFRESH_ICON_ANIMATION);

  const [refreshBadgeStyles, refreshBadgeApi] = useSpring(() => {
    const isStale = safeGet(charts, "payload.isStaleMetric", false);
    return {
      duration: 300,
      translateX: 0,
      translateY: 0,
      opacity: isStale ? 1 : 0.6,
      backgroundColor: "transparent",
      color: isStale ? colors.anomalyIndicator : colors.paleYellow,
    };
  });

  const slideDownInitialState = { paddingTop: 0, duration: 300 };
  const [slideDownStyles, slideDownApi] = useSpring(() => slideDownInitialState);

  useEffect(() => {
    if (isRefreshClicked) {
      refreshBadgeApi({
        translateX:
          window.innerWidth / 2 - safeGet(badgeRef, "current.width", 0) / 2 - safeGet(badgeRef, "current.left", 0),
        translateY: 20,
        opacity: 1,
        backgroundColor: colors.canaryHighlight,
        color: colors.l2Background,
      });
      slideDownApi({
        paddingTop: 20,
      });
    } else {
      const isStale = safeGet(charts, "payload.isStaleMetric", false);
      refreshBadgeApi({
        translateX: 0,
        translateY: 0,
        opacity: isStale ? 1 : 0.6,
        backgroundColor: "transparent",
        color: isStale ? colors.anomalyIndicator : colors.paleYellow,
      });
      slideDownApi(slideDownInitialState);
    }
  }, [isRefreshClicked]);

  // ********** Animations End ************

  // Don't show previous metric name while data is being fetched for new one (Happens rarely)
  const metricName = metricOrPinId == charts.payload?.id ? charts.payload?.label : "";

  return (
    <div>
      {/*<div></div>*/}
      {/* Top Navigation Bar */}
      <div style={{ padding: "10px", backgroundColor: "white" }}>
        <TopNavbar isHome={false} handleClick={onBack} headerText={metricName} rootStyle="padding-bottom: 0;">
          {options.map((option) => (
            <MenuItem
              key={option.iconClass}
              {...option}
              showMarker={option.iconClass === "sliders" && isFilterApplied}
            />
          ))}
        </TopNavbar>
      </div>
      {/* Main Body */}
      <div className="details-container-imcom">
        <animated.div style={slideDownStyles} className="details-body-imcom">
          {/*<Tabs*/}
          {/*  tabs={RunningAnobisMode.SHOW_IM_COM_TIME_TAB.map((tabName) => ({*/}
          {/*    title: tabName,*/}
          {/*    key: tabName,*/}
          {/*  }))}*/}
          {/*  page={selectedTab}*/}
          {/*  onChange={handleChangeTab}*/}
          {/*>*/}
          {/*{RunningAnobisMode.SHOW_IM_COM_TIME_TAB.map((tabName) => (*/}
          <div key={selectedTab}>
            <div className="detail-content-imcom">
              {/* Details Scroller */}
              <DetailsScroller data={TOP_CHARTS} charts={charts.payload} loading={charts.pending} />

              {devLogging("nested-container charts", charts, distributedFilters, selectedDimensions)}
            </div>
            {/* Table Charts */}
            <div>
              {isInitialLoading ? (
                <Spin style={{ display: "block", margin: "20px auto" }} />
              ) : tableCharts.success ? (
                <MobileTable
                  timeTab={selectedTab}
                  metaData={charts.payload}
                  data={tableCharts}
                  allowGoingNextPage={false}
                  userRolesPayload={userRoles.payload}
                />
              ) : (
                <Spin style={{ display: "block", margin: "20px auto" }} />
              )}
            </div>
          </div>
          {/*))}*/}
          {/*</Tabs>*/}
        </animated.div>
      </div>
    </div>
  );
};

export default React.memo(DetailsContainer);
